"use client";

import { useCallback, useEffect, useState } from "react";
import type { OrganizationOnboardingStatusResponse } from "@/@sdk/user-management/user-management.controller-models";
import { useSDKActionWithDeps } from "@/@sdk/sdk.hooks";
import { ExpressWizardShell } from "../shared/express-wizard-shell";
import { WorkspaceWelcomeStep } from "./steps/welcome-step";
import { WorkspaceStructureStep } from "./steps/structure-step";
import { WorkspaceBasicsStep } from "./steps/workspace-setup-steps";
import { BrandLoopStep } from "./steps/brand-loop-step";
import {
  WorkspaceTeamStep,
  WorkspaceStripeStep,
  WorkspaceSuppliersStep,
  WorkspaceCustomerPaymentMethodsStep,
  WorkspaceSupplierPaymentMethodsStep,
} from "./steps/workspace-integration-steps";
import { WorkspaceDoneStep } from "./steps/done-step";

const WORKSPACE_WIZARD_STEPS = [
  { id: "welcome", label: "Welcome" },
  { id: "structure", label: "Structure" },
  { id: "workspace-basics", label: "Workspace" },
  { id: "brand-loop", label: "Brands" },
  { id: "team", label: "Team" },
  { id: "integrations-stripe", label: "Stripe" },
  { id: "suppliers", label: "Suppliers" },
  { id: "customer-payment-methods", label: "Payments" },
  { id: "supplier-payment-methods", label: "Pay out" },
  { id: "done", label: "Done" },
] as const;

export type WorkspaceWizardStepId =
  (typeof WORKSPACE_WIZARD_STEPS)[number]["id"];

type ExpressWorkspaceOnboardingWizardProps = {
  open: boolean;
  status: OrganizationOnboardingStatusResponse;
  initialStepId?: WorkspaceWizardStepId;
  onDismiss: () => void;
  onStatusChange: (status: OrganizationOnboardingStatusResponse) => void;
};

export function ExpressWorkspaceOnboardingWizard({
  open,
  status,
  initialStepId = "welcome",
  onDismiss,
  onStatusChange,
}: ExpressWorkspaceOnboardingWizardProps) {
  const [currentStepId, setCurrentStepId] =
    useState<WorkspaceWizardStepId>(initialStepId);
  const [operatesMultipleBrands, setOperatesMultipleBrands] = useState(
    status.operatesMultipleBrands === true
  );
  const [completedBrandIds, setCompletedBrandIds] = useState<string[]>(
    status.brandsOnboardingCompleted ?? []
  );
  const [isBrandWizardActive, setIsBrandWizardActive] = useState(false);
  const [isFinishing, setIsFinishing] = useState(false);

  useEffect(() => {
    setCompletedBrandIds(status.brandsOnboardingCompleted ?? []);
  }, [status.brandsOnboardingCompleted]);

  const { doAction: patchOnboarding } = useSDKActionWithDeps(
    () => ({
      action: (sdk) => (body: Record<string, unknown>) =>
        sdk.patchOrganizationOnboarding(body),
    }),
    []
  );

  const persistStep = useCallback(
    async (
      stepId: WorkspaceWizardStepId,
      extra?: Record<string, unknown>
    ) => {
      const nextStatus = await patchOnboarding({
        workspaceWizardLastStepId: stepId,
        ...extra,
      });
      if (nextStatus) onStatusChange(nextStatus);
    },
    [onStatusChange, patchOnboarding]
  );

  const goToStep = useCallback(
    async (stepId: WorkspaceWizardStepId, extra?: Record<string, unknown>) => {
      setCurrentStepId(stepId);
      await persistStep(stepId, extra);
    },
    [persistStep]
  );

  const handleContinueLater = useCallback(async () => {
    const nextStatus = await patchOnboarding({
      workspaceWizardDismissedAt: Date.now(),
      workspaceWizardLastStepId: currentStepId,
      operatesMultipleBrands,
    });
    if (nextStatus) onStatusChange(nextStatus);
    onDismiss();
  }, [
    currentStepId,
    onDismiss,
    onStatusChange,
    operatesMultipleBrands,
    patchOnboarding,
  ]);

  const handleFinishSetup = useCallback(async () => {
    setIsFinishing(true);
    try {
      const nextStatus = await patchOnboarding({
        workspaceWizardCompleted: true,
        workspaceWizardLastStepId: "done",
        workspaceWizardDismissedAt: null,
      });
      if (nextStatus) onStatusChange(nextStatus);
      onDismiss();
    } finally {
      setIsFinishing(false);
    }
  }, [onDismiss, onStatusChange, patchOnboarding]);

  const stepIndex = WORKSPACE_WIZARD_STEPS.findIndex(
    (step) => step.id === currentStepId
  );
  const isDoneStep = currentStepId === "done";
  const isBrandLoopStep = currentStepId === "brand-loop";

  const stepTitle =
    currentStepId === "welcome"
      ? "Welcome to your workspace"
      : currentStepId === "structure"
        ? "Agency structure"
        : currentStepId === "workspace-basics"
          ? "Workspace details"
          : currentStepId === "brand-loop"
            ? "Set up your brand"
            : currentStepId === "team"
              ? "Invite your team"
              : currentStepId === "integrations-stripe"
                ? "Connect Stripe"
                : currentStepId === "suppliers"
                  ? "Add suppliers"
                  : currentStepId === "customer-payment-methods"
                    ? "Customer payments"
                    : currentStepId === "supplier-payment-methods"
                      ? "Supplier payments"
                      : "You're all set";

  const canContinueFromBrandLoop =
    !isBrandLoopStep || completedBrandIds.length > 0;

  const handleBrandCompleted = useCallback(
    async (brandId: string) => {
      const nextIds = [...new Set([...completedBrandIds, brandId])];
      setCompletedBrandIds(nextIds);
      const nextStatus = await patchOnboarding({
        brandsOnboardingCompleted: nextIds,
      });
      if (nextStatus) onStatusChange(nextStatus);
    },
    [completedBrandIds, onStatusChange, patchOnboarding]
  );

  const hidePrimaryAction =
    currentStepId === "workspace-basics" ||
    (isBrandLoopStep && isBrandWizardActive);

  return (
    <ExpressWizardShell
      open={open}
      title={stepTitle}
      description="Express workspace onboarding"
      steps={[...WORKSPACE_WIZARD_STEPS]}
      currentStepId={currentStepId}
      blockDismiss
      showBack={stepIndex > 0}
      isLastStep={isDoneStep}
      hidePrimaryAction={hidePrimaryAction}
      isNextLoading={isFinishing}
      isNextDisabled={
        (isBrandLoopStep && !canContinueFromBrandLoop) || isFinishing
      }
      onBack={() => {
        const prev = WORKSPACE_WIZARD_STEPS[stepIndex - 1];
        if (prev) void goToStep(prev.id);
      }}
      onNext={() => {
        if (currentStepId === "structure") {
          void goToStep("workspace-basics", { operatesMultipleBrands });
          return;
        }
        const next = WORKSPACE_WIZARD_STEPS[stepIndex + 1];
        if (next) void goToStep(next.id);
      }}
      onFinish={() => void handleFinishSetup()}
      onContinueLater={() => void handleContinueLater()}
      nextLabel={
        currentStepId === "welcome" ? "Get started" : "Continue"
      }
      finishLabel="Finish setup"
      dialogContentClassName="max-w-3xl"
    >
      {currentStepId === "welcome" ? <WorkspaceWelcomeStep /> : null}

      {currentStepId === "structure" ? (
        <WorkspaceStructureStep
          operatesMultipleBrands={operatesMultipleBrands}
          onChange={setOperatesMultipleBrands}
        />
      ) : null}

      {currentStepId === "workspace-basics" ? (
        <WorkspaceBasicsStep
          onSaved={() => void goToStep("brand-loop")}
        />
      ) : null}

      {currentStepId === "brand-loop" ? (
        <BrandLoopStep
          operatesMultipleBrands={operatesMultipleBrands}
          completedBrandIds={completedBrandIds}
          brandWizardProgress={status.brandWizardProgress}
          onBrandCompleted={(brandId) => void handleBrandCompleted(brandId)}
          onWizardActiveChange={setIsBrandWizardActive}
        />
      ) : null}

      {currentStepId === "team" ? <WorkspaceTeamStep /> : null}

      {currentStepId === "integrations-stripe" ? <WorkspaceStripeStep /> : null}

      {currentStepId === "suppliers" ? <WorkspaceSuppliersStep /> : null}

      {currentStepId === "customer-payment-methods" ? (
        <WorkspaceCustomerPaymentMethodsStep />
      ) : null}

      {currentStepId === "supplier-payment-methods" ? (
        <WorkspaceSupplierPaymentMethodsStep />
      ) : null}

      {currentStepId === "done" ? <WorkspaceDoneStep /> : null}
    </ExpressWizardShell>
  );
}
